// A service factory that creates a new service instance
import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios'
import { BackendServiceName } from 'v2/types'
import type { RUserSession } from 'v2/types/api/auth'
import { logger } from 'v2/utils/logger'
import { getLocalAuth } from '../../utils'
import { createSSEStream, type StreamFunction } from '../../utils/sse-stream'
import { getEmbedSession, isEmbedMode } from '../utils/embed-session'
import {
  applyEmbedRequestConfig,
  createEmbedHeaders,
  handleEmbedUnauthorized,
} from './service-interceptors'
import { applyTdlcTransport, isTdlcApiTransportEnabled } from './tdlc-transport'

const GLOBAL_API_URL = process.env.REACT_APP_GLOBAL_AUTH
const REGIONAL_API_URL = process.env.REACT_APP_REGION_AUTH

export interface Service {
  instance: AxiosInstance
  get: <T>(
    path: string,
    params?: Record<string, unknown> | string,
    config?: AxiosRequestConfig,
  ) => Promise<T>
  post: <TApiResponse>(
    path: string,
    data?: unknown,
    params?: Record<string, unknown>,
    config?: AxiosRequestConfig,
  ) => Promise<TApiResponse>
  put: <TApiResponse>(path: string, data?: unknown) => Promise<TApiResponse>
  patch: <TApiResponse>(path: string, data?: unknown) => Promise<TApiResponse>
  delete: <T>(path: string, data?: unknown, config?: AxiosRequestConfig) => Promise<T>
  stream: StreamFunction
}

export const createGlobalHeaders = () => {
  // Embed: identity from the embed session (Client-Type embed_app, no Aom_uuid/Browser-Session-Id).
  if (isEmbedMode()) {
    return createEmbedHeaders()
  }
  const auth: RUserSession = getLocalAuth()
  return {
    'Content-Type': 'application/json',
    'Client-Type': 'web_app',
    Aom_uuid: auth.aom_uuid,
    Authorization: `Token ${auth.auth_token}`,
    'Browser-Session-Id': auth.browser_session_id,
    Region: auth.region,
  }
}

// The active region: from the embed session in embed mode, else from localStorage auth.
export const getActiveRegion = (): string | undefined =>
  isEmbedMode() ? getEmbedSession()?.region : getLocalAuth().region

/**
 * Resolves the base URL with region replacement for direct fetch calls.
 * This is useful when making fetch requests that bypass axios interceptors.
 * @param regional - Whether to use regional API URL (default: true)
 * @returns The resolved base URL with region placeholder replaced
 */
export const resolveBaseUrl = (regional = true): string => {
  let baseURL = (regional ? REGIONAL_API_URL : GLOBAL_API_URL) || ''

  if (baseURL.includes('{{region}}')) {
    const region = getActiveRegion()
    if (!region) {
      throw new Error('Region is not set, cannot resolve base URL')
    }
    baseURL = baseURL.replace('{{region}}', region)
  }

  return baseURL
}

// 401s meaning "this resource refused you", not "session gone". Embed adds external-apps (older
// gateways forward plivocx 401s) and rag-service (transient KB 401s right after exchange).
const RESOURCE_SCOPED_401_PATHS = ['plivo-api', '/tdlc/', '/verify/']
// TODO: on an embed 401, re-probe v1/embed/session/me instead of listing routes.
const EMBED_RESOURCE_SCOPED_401_PATHS = ['external-apps-service', 'rag-service']

export const isResourceScoped401Url = (url: string | undefined, embed: boolean): boolean => {
  if (!url) {
    return false
  }
  const paths = embed
    ? [...RESOURCE_SCOPED_401_PATHS, ...EMBED_RESOURCE_SCOPED_401_PATHS]
    : RESOURCE_SCOPED_401_PATHS
  return paths.some((path) => url.includes(path))
}

// Create a new service instance
/**
 * Creates a service instance with axios for making HTTP requests.
 * @param resource - The resource location for the service.
 * @param regional - A boolean indicating whether to use the regional API URL or the global API URL.
 * @returns The service instance with REST methods.
 * @example
 * const service = createService('users')
 * service.get<User[]>('list', { page: 1, limit: 10 })
 */
export function createService(
  resource = '',
  regional = true,
  backendServiceName = BackendServiceName.default,
  version = '',
  domain = '',
): Service {
  const baseURL = `${
    regional ? REGIONAL_API_URL : GLOBAL_API_URL
  }${version ? `${version}/` : ''}${backendServiceName ? `${backendServiceName}` : ''}${resource}`
  const instance = axios.create({
    baseURL: domain || baseURL,
    timeout: 60000, // 60 seconds - prevents "timeout of 0ms exceeded" errors
  })

  // Interceptors
  // Request interceptor to append the global headers
  instance.interceptors.request.use(
    (config) => {
      // Embed: rewrite onto the embed gateway with the embed session's region and headers.
      if (isEmbedMode()) {
        return applyEmbedRequestConfig(config)
      }
      const auth = getLocalAuth()

      if (config.baseURL?.includes('{{region}}') && !auth.region) {
        throw new Error('Region is not set, cannot make request')
      }

      // Per-org opt-in: on /tdlc/ calls the flag is the sole authority over X-TDLC-Transport (set
      // when enrolled, strip any caller casing otherwise). Off (default) => absent => SDK routing.
      return {
        ...config,
        baseURL: config.baseURL?.replace('{{region}}', auth.region),
        headers: applyTdlcTransport(
          { ...createGlobalHeaders(), ...config.headers },
          config.url,
          isTdlcApiTransportEnabled(),
        ),
      }
    },
    (error) => Promise.reject(error),
  )

  instance.interceptors.response.use(
    (response) => {
      // axios 0.21 settles a dropped request (status 0) as a success with body '', and callers
      // then read fields off ''. Reject it the way axios rejects its own network errors.
      if (response.status === 0) {
        const error = Object.assign(new Error('Network Error'), {
          config: response.config,
          request: response.request,
          isAxiosError: true,
        })
        return Promise.reject(error)
      }
      return response
    },
    (error) => {
      const isLogoutUrl = error.response?.config.url?.includes('logout')
      const isLoginUrl = error.response?.config.url?.includes('login')
      const isAuthFlowUrl =
        error.response?.config.url?.includes('phone-verification') ||
        error.response?.config.url?.includes('verify-otp') ||
        error.response?.config.url?.includes('activate-invite')
      // Resource-level 401s (Plivo proxy / 10DLC / Verify) mean the org/subaccount
      // Plivo credential or AOM check failed for THIS resource, not that the session
      // expired. Let the mutation onError surface a toast instead of logging out.
      // '/verify/' matches only the Verify product endpoints — the auth flows are
      // hyphenated (verify-otp, verify-user) and stay on the logout path above.
      const isResourceScoped401 = isResourceScoped401Url(error.response?.config.url, isEmbedMode())

      // Embed: any other 401 means the embed session died. The overlay and parent handle it, never
      // window.cx.logout (that would log out a console tab).
      if (!isResourceScoped401 && handleEmbedUnauthorized(error.response?.status)) {
        return Promise.reject(new Error('Response Interceptor - 401 - embed session expired'))
      }

      if (
        error.response?.status === 401 &&
        !isLogoutUrl &&
        !isLoginUrl &&
        !isAuthFlowUrl &&
        !isResourceScoped401
      ) {
        window.cx.logout(true)
        return Promise.reject(new Error('Response Interceptor - 401 Unauthorized - Logging out'))
      }

      // Ignore common client-side errors that are part of normal flow
      const ignoredStatusCodes = [
        400, // Bad Request — server validation; UI surfaces via toast
        401, // Unauthorized
        403, // Forbidden
        404, // Not Found
        422, // Unprocessable Entity
        409, // Conflict
        429, // Too Many Requests
      ]

      if (!ignoredStatusCodes.includes(error.response?.status)) {
        // If the error is a 500 error, log it as a critical error
        const is500Error = error.response?.status >= 500
        logger[is500Error ? 'critical' : 'error'](error, {
          url: error.response?.config.url,
          status: error.response?.status,
          data: error.response?.data,
        })
      }

      return Promise.reject(error)
    },
  )

  // Default REST methods
  const methods: Omit<Service, 'instance' | 'stream'> = {
    get: (path, params, config = {}) =>
      instance.get(path, { params, ...config }).then((res) => res.data),
    post: (path, data, params, config = {}) =>
      instance.post(path, data, { params, ...config }).then((res) => res.data),
    put: (path, data) => instance.put(path, data).then((res) => res.data),
    patch: (path, data) => instance.patch(path, data).then((res) => res.data),
    delete: (path, data, config = {}) =>
      instance.delete(path, { data, ...config }).then((res) => res.data),
  }

  // Add SSE streaming method
  const stream: StreamFunction = (path, body, handlers) =>
    createSSEStream(instance.defaults.baseURL || '', path, body, handlers)

  return {
    instance,
    ...methods,
    stream,
  }
}

export const extractBlob = (response: unknown): Blob => {
  if (response instanceof Blob) return response
  if (
    response &&
    typeof response === 'object' &&
    'data' in response &&
    response.data instanceof Blob
  ) {
    return response.data
  }
  throw new Error(`Expected Blob response, got ${typeof response}`)
}
